import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import PasswordStrength from '../components/PasswordStrength';

export default function UsersPage() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [form, setForm] = useState({ username:'', full_name:'', email:'', password:'', role:'user' });
  const [saving, setSaving] = useState(false);

  // Edit user state
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ username:'', full_name:'', email:'', role:'user' });
  const [editSaving, setEditSaving] = useState(false);

  const load = () => { setLoading(true); api.get('/users/list.php').then(r => { if(r.data.success) setUsers(r.data.data); }).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.username || !form.password) { toast('Username and password required', 'error'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/users/create.php', form);
      if (data.success) { toast(data.message); setShowAdd(false); setForm({ username:'', full_name:'', email:'', password:'', role:'user' }); load(); }
      else toast(data.error, 'error');
    } finally { setSaving(false); }
  };

  const handleToggle = async u => {
    const { data } = await api.post('/users/toggle_active.php', { id: u.id, is_active: u.is_active ? 0 : 1 });
    if (data.success) { toast(data.message); load(); } else toast(data.error, 'error');
  };

  const handleResetPw = async () => {
    if (!resetTarget || !resetPw) return;
    setResetLoading(true);
    try {
      const { data } = await api.post('/users/reset_password.php', { id: resetTarget.id, new_password: resetPw });
      if (data.success) { toast(data.message); setResetTarget(null); setResetPw(''); }
      else toast(data.error, 'error');
    } finally { setResetLoading(false); }
  };

  const handleDelete = async u => {
    if (!window.confirm(`Delete user "${u.username}"?`)) return;
    const { data } = await api.post('/users/delete.php', { id: u.id });
    if (data.success) { toast(data.message); load(); } else toast(data.error, 'error');
  };

  const openEdit = u => {
    setEditTarget(u);
    setEditForm({ username: u.username, full_name: u.full_name||'', email: u.email||'', role: u.role });
  };

  const handleEditSave = async () => {
    if (!editForm.username.trim()) { toast('Username cannot be empty', 'error'); return; }
    setEditSaving(true);
    try {
      const { data } = await api.post('/users/admin_update.php', { id: editTarget.id, ...editForm });
      if (data.success) { toast(data.message); setEditTarget(null); load(); }
      else toast(data.error, 'error');
    } finally { setEditSaving(false); }
  };

  const initials = name => (name||'?').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">User Management</div><div className="page-sub">Admin-only: create users, edit details, reset passwords, manage access</div></div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>＋ Add New User</button>
      </div>

      <div className="page-body" style={{ display:'flex', flexDirection:'column', gap:16 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
          {[
            { l:'Total Users', v: users.length },
            { l:'Active', v: users.filter(u=>u.is_active).length },
            { l:'Admins', v: users.filter(u=>u.role==='admin').length },
            { l:'Pending Pw Change', v: users.filter(u=>u.must_change_password).length },
          ].map(s => (
            <div key={s.l} style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--text2)', textTransform:'uppercase' }}>{s.l}</div>
              <div style={{ fontSize:22, fontWeight:700, color:'var(--navy)', marginTop:3 }}>{s.v}</div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="tbl-wrap">
            <table>
              <thead><tr>{['User','Email','Role','Status','Last Login','Pw Status','Actions'].map(h=><th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} style={{textAlign:'center',padding:24,color:'var(--text2)'}}>Loading…</td></tr>
                : users.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:30, height:30, borderRadius:'50%', background:'var(--teal-light)', color:'var(--teal)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700 }}>{initials(u.full_name||u.username)}</div>
                        <div><div style={{ fontWeight:600, fontSize:13 }}>{u.full_name||u.username}</div><div style={{ fontSize:11, color:'var(--text2)' }}>@{u.username}</div></div>
                      </div>
                    </td>
                    <td style={{ color:'var(--text2)' }}>{u.email || '—'}</td>
                    <td><span className={`badge ${u.role==='admin'?'badge-purple':'badge-slate'}`}>{u.role}</span></td>
                    <td><button onClick={() => handleToggle(u)} className={`badge ${u.is_active?'badge-green':'badge-red'}`} style={{ border:'none', cursor:'pointer' }}>{u.is_active?'Active':'Disabled'}</button></td>
                    <td style={{ fontSize:11, color:'var(--text2)' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString('en-GB') : 'Never'}</td>
                    <td>{u.must_change_password ? <span className="badge badge-amber">Must Change</span> : <span className="badge badge-green">OK</span>}</td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>✏️ Edit</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setResetTarget(u); setResetPw(''); }}>🔑 Reset Pw</button>
                        <button className="btn-icon danger" onClick={() => handleDelete(u)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" style={{ maxWidth:440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Add New User</span><button onClick={() => setShowAdd(false)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:18 }}>✕</button></div>
            <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div className="form-grid cols-2">
                <div><label className="lbl">Username *</label><input className="input" value={form.username} onChange={e => setForm(f=>({...f,username:e.target.value}))} placeholder="e.g. john.doe" /></div>
                <div><label className="lbl">Role</label><select className="input" value={form.role} onChange={e => setForm(f=>({...f,role:e.target.value}))}><option value="user">User</option><option value="admin">Admin</option></select></div>
              </div>
              <div><label className="lbl">Full Name</label><input className="input" value={form.full_name} onChange={e => setForm(f=>({...f,full_name:e.target.value}))} placeholder="e.g. John Doe" /></div>
              <div><label className="lbl">Email</label><input type="email" className="input" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} placeholder="e.g. john@company.com" /></div>
              <div>
                <label className="lbl">Initial Password *</label>
                <input type="password" className="input" value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} placeholder="Temporary password" />
                <PasswordStrength password={form.password} />
                <p style={{ fontSize:11, color:'var(--text2)', marginTop:6 }}>User must change it on first login.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving?'Creating…':'Create User'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editTarget && (
        <div className="modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="modal" style={{ maxWidth:440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Edit User</span><button onClick={() => setEditTarget(null)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:18 }}>✕</button></div>
            <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div className="form-grid cols-2">
                <div><label className="lbl">Username *</label><input className="input" value={editForm.username} onChange={e => setEditForm(f=>({...f,username:e.target.value}))} placeholder="e.g. john.doe" /></div>
                <div><label className="lbl">Role</label><select className="input" value={editForm.role} onChange={e => setEditForm(f=>({...f,role:e.target.value}))}><option value="user">User</option><option value="admin">Admin</option></select></div>
              </div>
              <div><label className="lbl">Full Name</label><input className="input" value={editForm.full_name} onChange={e => setEditForm(f=>({...f,full_name:e.target.value}))} placeholder="e.g. John Doe" /></div>
              <div><label className="lbl">Email</label><input type="email" className="input" value={editForm.email} onChange={e => setEditForm(f=>({...f,email:e.target.value}))} placeholder="e.g. john@company.com" /></div>
              <div className="alert alert-info" style={{ fontSize:12 }}>ℹ️ Editing details for <strong>@{editTarget.username}</strong>. Password is unchanged here — use Reset Pw for that.</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditSave} disabled={editSaving}>{editSaving?'Saving…':'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="modal-overlay" onClick={() => { setResetTarget(null); setResetPw(''); }}>
          <div className="modal" style={{ maxWidth:380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Reset Password</span><button onClick={() => { setResetTarget(null); setResetPw(''); }} style={{ background:'none', border:'none', cursor:'pointer', fontSize:18 }}>✕</button></div>
            <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div className="alert alert-warning">Resetting password for <strong>{resetTarget.full_name||resetTarget.username}</strong>. They must change it on next login.</div>
              <div>
                <label className="lbl">New Temporary Password *</label>
                <input type="password" className="input" value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="Set a new password" />
                <PasswordStrength password={resetPw} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setResetTarget(null); setResetPw(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleResetPw} disabled={resetLoading}>{resetLoading?'Resetting…':'Reset Password'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
